/**
 * Shared fuel-type helpers: CMS product_id → petrol / electric / … (see Buy vignette Enter details).
 */

export const DEFAULT_PRODUCT_ICON_IDS = {
    'electric-vehicle': [11, 15, 19, 23],
    'plugin-hybrid': [12, 16, 20, 24],
    'cng-or-biomethane': [13, 17, 21, 25],
    'petrol-or-diesel': [14, 18, 22, 26]
};

export function productIdMatchesList(productId, ids) {
    const list = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
    return list.some(
        (id) => Number(id) === Number(productId) || String(id) === String(productId)
    );
}

export function productIdToFuelTypeKey(productId, idMap) {
    const map =
        idMap && typeof idMap === 'object' && Object.keys(idMap).length
            ? idMap
            : DEFAULT_PRODUCT_ICON_IDS;
    for (const [key, ids] of Object.entries(map)) {
        if (productIdMatchesList(productId, ids)) return key;
    }
    return null;
}

/** Same SVGs for every locale (English asset paths on site origin). */
const FUEL_TYPE_FIXED_ICON_PATHS = {
    'petrol-or-diesel': '/img/svg/local_gas_station.svg',
    'cng-or-biomethane': '/img/svg/eco.svg',
    'plugin-hybrid': '/img/svg/electriccar.svg',
    'electric-vehicle': '/img/svg/electric-bolt.svg'
};

/** Public path for fuel row icon, or '' if unknown product id. */
export function fixedFuelRowIconPath(productId, idMap) {
    const key = productIdToFuelTypeKey(productId, idMap);
    if (!key) return '';
    return FUEL_TYPE_FIXED_ICON_PATHS[key] || '';
}

/**
 * Display order of fuel rows per vignette duration (CMS page_id / row id from API).
 * 1-day → 10-day → 30-day → 1-year; order within each group is left-to-right as listed.
 */
const VIGNETTE_DURATION_ROW_ORDER = [
    [72, 71, 70, 69],
    [77, 76, 75, 74],
    [82, 81, 80, 79],
    [87, 86, 85, 84],
];

function resolveVignetteRowOrderKey(product) {
    if (!product || typeof product !== 'object') return null;
    const raw =
        product.page_id ??
        product.pageId ??
        product.category?.page_id ??
        product.category?.id ??
        product.id;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/** Sort index 0..3 within duration; 999 = unknown (use title-based fallback). */
export function vignetteFuelRowOrderIndex(product) {
    const key = resolveVignetteRowOrderKey(product);
    if (key == null) return 999;
    for (const order of VIGNETTE_DURATION_ROW_ORDER) {
        const idx = order.indexOf(key);
        if (idx !== -1) return idx;
    }
    return 999;
}

/** Works for EnterDetails rows ({ translatedTitle }) and API products ({ translated.title }). */
export function fuelProductDisplayTitle(p) {
    if (!p || typeof p !== 'object') return '';
    return p.translatedTitle ?? p.translated?.title ?? p.name ?? '';
}

/** Fixed order for "Select fuel type": Petrol/Diesel → CNG → Plug-in hybrid → Electric */
export function fuelTypeOptionSortIndex(translatedTitle) {
    const s = String(translatedTitle || '').toLowerCase();
    const isPetrolDiesel =
        (s.includes('petrol') ||
            s.includes('diesel') ||
            s.includes('benzine') ||
            (s.includes('gasoline') && !s.includes('cng'))) &&
        !s.includes('hybrid') &&
        !s.includes('hybride') &&
        !s.includes('electric') &&
        !s.includes('elektrisch') &&
        !s.includes('plug');
    if (isPetrolDiesel) return 0;
    if (s.includes('cng') || s.includes('biomethane')) return 1;
    if (
        (s.includes('hybrid') || s.includes('hybride')) &&
        (s.includes('plug') || s.includes('plugin'))
    ) {
        return 2;
    }
    if (s.includes('electric') || s.includes('elektrisch')) return 3;
    return 99;
}
